import { useState } from 'react';
import { faArrowRight, faArrowsLeftRight, faHouse } from '@fortawesome/free-solid-svg-icons';
import { useProspects, useUpdateProspect } from '../../queries/prospects';
import { usePhone } from '../../hooks/usePhone';
import { BOARD_STAGES, columnOf, daysInStage, stageForColumn, whenOf } from '../../utils/prospects';
import { Av, I, Seg, cx } from '../Todos/ui';
import ProspectDrawer from './ProspectDrawer';

const daysLabel = (p) => {
  const d = daysInStage(p);
  return d == null ? '—' : d === 0 ? 'Today' : `${d}d`;
};

function Card({ p, compact, onOpen, onDragStart }) {
  const unit = p.interestedProperty || 'No unit yet';
  if (compact) {
    return (
      <button type="button" className="pl2-card cmp" draggable={Boolean(onDragStart)} onDragStart={onDragStart} onClick={onOpen} aria-label={`${p.name}, ${p.pipelineStage}`}>
        <div className="m-row">
          <span className="b sm pl2-name grow">{p.name}</span>
          <span className="pl2-days num">{daysLabel(p)}</span>
        </div>
        <div className="pl2-unit">{unit}</div>
      </button>
    );
  }
  return (
    <button type="button" className="pl2-card" draggable={Boolean(onDragStart)} onDragStart={onDragStart} onClick={onOpen} aria-label={`${p.name}, ${p.pipelineStage}`}>
      <div className="m-row">
        <Av name={p.name} cls="lt sm" />
        <span className="b sm pl2-name grow">{p.name}</span>
        <span className="pl2-days num">{daysLabel(p)}</span>
      </div>
      <div className="pl2-unit">
        <I icon={faHouse} />
        <span>{unit}</span>
      </div>
      <div className="pl2-meta">
        <span>{p.assignedTo || 'Unassigned'}</span>
      </div>
      <div className="pl2-next">
        <I icon={faArrowRight} />
        <span>{whenOf(p)}</span>
      </div>
    </button>
  );
}

export default function LeasingBoard() {
  const phone = usePhone();
  const { rows } = useProspects();
  const update = useUpdateProspect();
  const [compact, setCompact] = useState('standard');
  const [openId, setOpenId] = useState(null);
  const [sel, setSel] = useState(null);
  const [over, setOver] = useState(null);

  const inCol = (col) => rows.filter((p) => columnOf(p) === col);
  const open = rows.find((p) => p._id === openId);

  const drop = (col, id) => {
    const p = rows.find((x) => x._id === id);
    const stage = stageForColumn(col);
    if (!p || !stage || columnOf(p) === col) return;
    update.mutate({ id, pipelineStage: stage, stageAt: new Date().toISOString() });
  };

  if (phone) {
    const col = sel || BOARD_STAGES.find((s) => inCol(s.col).length)?.col || BOARD_STAGES[0].col;
    const cards = inCol(col);
    return (
      <div className="m-col gap8">
        <Seg items={BOARD_STAGES.map((s) => [s.col, s.label, null, inCol(s.col).length])} value={col} onChange={setSel} label="Stage" />
        <div className="m-sub">Open a card to move it to another stage.</div>
        {cards.length ? (
          cards.map((p) => <Card key={p._id} p={p} onOpen={() => setOpenId(p._id)} />)
        ) : (
          <div className="pl2-empty">No one at this stage.</div>
        )}
        {open && <ProspectDrawer prospect={open} onClose={() => setOpenId(null)} />}
      </div>
    );
  }

  return (
    <div className="m-col gap8">
      <div className="m-row wrap">
        <div className="m-pipehint grow">
          <I icon={faArrowsLeftRight} />
          Drag a card to move it between stages. Scroll sideways to see all 8.
        </div>
        <Seg
          items={[
            ['standard', 'Standard'],
            ['compact', 'Compact']
          ]}
          value={compact}
          onChange={setCompact}
          label="Card size"
        />
      </div>
      <div className="pl2-board">
        {BOARD_STAGES.map((s) => {
          const cards = inCol(s.col);
          return (
            <div
              key={s.col}
              className={cx('pl2-col', over === s.col && 'over')}
              onDragOver={(e) => {
                e.preventDefault();
                if (over !== s.col) setOver(s.col);
              }}
              onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) setOver(null);
              }}
              onDrop={(e) => {
                e.preventDefault();
                setOver(null);
                drop(s.col, e.dataTransfer.getData('text/plain'));
              }}
            >
              <div className="pl2-colh">
                <span className="b sm">{s.label}</span>
                <span className="m-tc num">{cards.length}</span>
              </div>
              <div className="pl2-colb">
                {cards.length ? (
                  cards.map((p) => (
                    <Card
                      key={p._id}
                      p={p}
                      compact={compact === 'compact'}
                      onOpen={() => setOpenId(p._id)}
                      onDragStart={(e) => e.dataTransfer.setData('text/plain', p._id)}
                    />
                  ))
                ) : (
                  <div className="pl2-empty">Drop a card here</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {open && <ProspectDrawer prospect={open} onClose={() => setOpenId(null)} />}
    </div>
  );
}
